/**
 * La progression en compréhension de l'écrit entre septembre et un QCM de suivi passé sur une FORME
 * PARALLÈLE (11/10/2026). Les textes diffèrent : on ne compare pas question par question, mais par support
 * (texte littéraire, support documentaire) et par compétence (information explicite, inférences, sens global),
 * pour la classe et pour chaque élève, et le groupe de l'élève : celui de septembre (le ministère), puis celui
 * du QCM de suivi (les seuils de l'outil, sur la forme complète seulement ; la forme de 4e, écrite pour TanoEd,
 * n'a pas de seuils étalonnés, donc pas de groupe).
 *
 * Les copies : en septembre, eleve.reponses[nom court du texte][Qnn] = { score } ; dans un QCM de suivi,
 * eleve.reponses[type_label][id de la question] = { score, question_num }. Les deux noms sont acceptés des deux
 * côtés. Une question laissée blanche compte comme fausse ; un élève sans copie ne compte pas.
 */
import { nomDuTexte } from '../EvalNat/evaluationConfig';
import { competenceDuLibelle, supportDuTexte } from '../../../generators/evalnat/formesParalleles';

export const SUPPORTS_ORDRE = ['litteraire', 'documentaire'];
export const COMPETENCES_ORDRE = ['explicite', 'inference', 'sens_global'];

const sansApostropheCourbe = (s) => String(s || '').replace(/’/g, "'");
const numero = (questionNum) => parseInt(String(questionNum ?? '').replace(/\D/g, ''), 10);
const cle = (nomCourt, questionNum) => `${nomCourt}|${numero(questionNum)}`;

/**
 * Les items de septembre que la forme remplace : (texte, numéro) → { support, competence }. La compétence se lit
 * dans le libellé de l'item (6e) ; sinon dans le classement que la forme donne de ses items (4e : le libellé du
 * ministère y décrit l'item, pas sa famille).
 */
export const itemsDeSeptembre = (questionsNat, forme) => {
    const remplaces = new Set(forme.remplace.map(sansApostropheCourbe));
    const items = new Map();
    (questionsNat || []).forEach((q) => {
        const typeLabel = sansApostropheCourbe(q.type_label);
        if (!remplaces.has(typeLabel)) return;
        const nomCourt = nomDuTexte(typeLabel);
        const competence = competenceDuLibelle(q.nom_label)
            || forme.competencesDeSeptembre?.[nomCourt]?.[numero(q.question_num) - 1];
        if (!competence) return;
        items.set(cle(nomCourt, q.question_num), { support: supportDuTexte(typeLabel), competence });
    });
    return items;
};

/** Les questions de la forme parallèle : (texte, numéro) → { support, competence }. */
export const itemsDeLaForme = (forme) => {
    const items = new Map();
    forme.textes.forEach((t) => t.questions.forEach((q) => {
        items.set(cle(nomDuTexte(t.typeLabel), q.questionNum), { support: t.support, competence: q.competence });
    }));
    return items;
};

const compteVide = () => ({ justes: 0, total: 0 });
const bilanVide = () => ({
    global: compteVide(),
    parSupport: Object.fromEntries(SUPPORTS_ORDRE.map((s) => [s, compteVide()])),
    parCompetence: Object.fromEntries(COMPETENCES_ORDRE.map((c) => [c, compteVide()])),
});
const ajouter = (compte, juste) => { compte.total += 1; if (juste) compte.justes += 1; };

/** Les justes et le total d'une copie sur ces items, par support et par compétence ; null sans réponse. */
export const compterCopie = (reponses, items) => {
    if (!reponses || typeof reponses !== 'object') return null;
    const resultat = bilanVide();
    Object.entries(reponses).forEach(([categorie, parQuestion]) => {
        if (!parQuestion || typeof parQuestion !== 'object') return; // un niveau (« Satisfaisant »), pas des réponses
        const nomCourt = nomDuTexte(sansApostropheCourbe(categorie));
        Object.entries(parQuestion).forEach(([cleQuestion, donnee]) => {
            const item = items.get(cle(nomCourt, donnee?.question_num ?? cleQuestion));
            if (!item) return;
            const juste = donnee?.score === 1 || donnee?.score === '1';
            ajouter(resultat.global, juste);
            ajouter(resultat.parSupport[item.support], juste);
            ajouter(resultat.parCompetence[item.competence], juste);
        });
    });
    return resultat.global.total > 0 ? resultat : null;
};

/** Le groupe de l'outil de positionnement, sur la forme complète seulement (sinon null) ; sans seuils, aucun. */
export const groupeDeLaForme = (copie, forme) => {
    if (!forme.seuils) return null;
    const totalForme = forme.textes.reduce((n, t) => n + t.questions.length, 0);
    if (!copie || copie.global.total !== totalForme) return null;
    if (copie.global.justes <= forme.seuils.aBesoinsMax) return 'À besoins';
    if (copie.global.justes <= forme.seuils.fragileMax) return 'Fragile';
    return 'Satisfaisant';
};

const additionner = (a, b) => {
    a.global.justes += b.global.justes; a.global.total += b.global.total;
    SUPPORTS_ORDRE.forEach((s) => { a.parSupport[s].justes += b.parSupport[s].justes; a.parSupport[s].total += b.parSupport[s].total; });
    COMPETENCES_ORDRE.forEach((c) => { a.parCompetence[c].justes += b.parCompetence[c].justes; a.parCompetence[c].total += b.parCompetence[c].total; });
};

/**
 * La progression de la classe et de chaque élève. La classe ne compte que les élèves qui ont les deux copies
 * (les mêmes élèves des deux côtés) ; chaque élève a ses deux copies, ou celle qu'il a.
 */
export const progressionComprehension = ({ evalNat, evalBilan, forme }) => {
    const itemsNat = itemsDeSeptembre(evalNat?.questions, forme);
    const itemsForme = itemsDeLaForme(forme);
    const copiesNat = new Map((evalNat?.eleves_reponses || []).map((e) => [`${e.nom_eleve}|${e.classe_nom}`, e]));
    const classe = { septembre: bilanVide(), bilan: bilanVide(), eleves: 0 };
    const eleves = (evalBilan?.eleves_reponses || []).map((e) => {
        const nat = copiesNat.get(`${e.nom_eleve}|${e.classe_nom}`);
        const septembre = compterCopie(nat?.reponses, itemsNat);
        const bilan = compterCopie(e.reponses, itemsForme);
        if (septembre && bilan) {
            additionner(classe.septembre, septembre);
            additionner(classe.bilan, bilan);
            classe.eleves += 1;
        }
        const niveauNat = nat?.reponses?.TestComprehensionEcrite;
        return {
            nom: e.nom_eleve,
            classe: e.classe_nom,
            septembre,
            bilan,
            groupeSeptembre: typeof niveauNat === 'string' && niveauNat.trim() ? niveauNat.trim() : null,
            groupeBilan: groupeDeLaForme(bilan, forme),
        };
    }).filter((e) => e.septembre || e.bilan);
    return { classe, eleves };
};

/** Le taux de réussite d'un compte, en pour cent arrondi, ou null s'il est vide. */
export const taux = (compte) => (compte && compte.total > 0 ? Math.round((100 * compte.justes) / compte.total) : null);

/**
 * La forme réduite aux textes que ce QCM de suivi pose, et septembre aux textes du même support (12/10/2026 : un
 * QCM sur le seul « Copiste » comparait 10 questions littéraires aux 21 de septembre, littéraires et
 * documentaires). Null si le QCM ne pose aucun texte de la forme.
 */
export const formeDuBilan = (forme, questions) => {
    if (!forme) return null;
    const poses = new Set((questions || []).map((q) => sansApostropheCourbe(q.type_label)));
    const textes = forme.textes.filter((t) => poses.has(sansApostropheCourbe(t.typeLabel)));
    if (textes.length === 0) return null;
    const supports = new Set(textes.map((t) => t.support));
    const remplace = forme.remplace.filter((tl) => supports.has(supportDuTexte(sansApostropheCourbe(tl))));
    return { ...forme, textes, remplace };
};

/** Le QCM de suivi passe-t-il une forme parallèle ? (au moins une de ses questions) */
export const bilanSurFormeParallele = (questions, forme) => {
    if (!forme) return false;
    const textes = new Set(forme.textes.map((t) => sansApostropheCourbe(t.typeLabel)));
    return (questions || []).some((q) => textes.has(sansApostropheCourbe(q.type_label)));
};
